'use client';

import { useEffect } from 'react';

import { useBehaviorSubject } from '@ps/functions/hooks/useBehaviorSubject';
import { useCurrentUserId } from '@ps/ui/contexts/auth';
import { FlashList } from '@ps/ui/organisms/flash-list';
import { Pin, X } from 'lucide-react';

import { useCommunityMessageContext } from '../../contexts/community-message/useCommunityMessageContext';
import { useCommunityContext } from '../../contexts/community/useCommunityContext';
import {
  usePinnedMessages,
  useRefreshPinnedMessages,
} from '../../hooks/usePinnedMessages';
import type { CommunityChannel } from '../../types/CommunityChannel';
import type { ExtendedCommunityChannelMessage } from '../../types/ExtendedCommunityChannelMessage';
import type { MessageInteractionMode } from '../../types/MessageInteractionMode';
import { ChannelMessageBubble } from '../channel-message-view/ChannelMessageBubble';

interface Props {
  channel: CommunityChannel;
  /** The viewer's interaction rights, forwarded to each pinned bubble. */
  mode?: MessageInteractionMode;
  onClose: () => void;
}

/**
 * Replaces the channel message list when the pinned banner is
 * activated. Pulls a pinned-only page from SearchApi and renders
 * pinned bubbles (via a recycling {@link FlashList}) in
 * pinned-at-desc order. Pin/un-pin events from the bubble kebab
 * trigger a refresh so the list stays in sync without a dedicated
 * pinned-Firestore-sub.
 *
 * Slice 3 doesn't yet decorate pinned bubbles with the orange
 * "Pinned by <name>" badge from the design (Image #6) — that's a
 * follow-up cosmetic.
 */
export function ChannelPinnedMessagesPane({ channel, mode, onClose }: Props) {
  const currentUserId = useCurrentUserId();
  // Pinned messages are fetched from the CHANNEL OWNER's store, not the
  // viewer's — on the owner surface these coincide, but an audience member has
  // a different `currentUserId`, so use the context `creatorId`.
  const { creatorId } = useCommunityContext();
  const state$ = usePinnedMessages(creatorId, channel.id);
  const [state] = useBehaviorSubject(state$);
  const refresh = useRefreshPinnedMessages(state$, creatorId, channel.id);

  // Refetch whenever a pin flips or a pinned message is deleted anywhere in the
  // channel. The per-message live listener fires this signal on pin edits (even
  // for older messages the main `messages$` stream never re-delivers), so the
  // pane stays in sync without its own pinned-Firestore subscription.
  const { pinnedMessagesInvalidated$ } = useCommunityMessageContext();
  useEffect(() => {
    const sub = pinnedMessagesInvalidated$.subscribe(() => void refresh());
    return () => sub.unsubscribe();
  }, [pinnedMessagesInvalidated$, refresh]);

  return (
    <section className="flex h-full min-h-0 flex-1 flex-col">
      <header className="border-line flex h-[68px] shrink-0 items-center gap-3 border-b px-4">
        <Pin className="text-brand-text h-5 w-5 rotate-45" aria-hidden />
        <h2 className="text-content-1 flex-1 text-sm font-semibold">
          Pinned Messages
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close pinned messages"
          className="hover:text-content-1 text-content-2"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>
      </header>

      {state.error ? (
        <p
          role="alert"
          className="bg-danger/10 m-4 rounded-md border border-red-500/30 px-3 py-2 text-xs text-red-200"
        >
          Couldn&rsquo;t load pinned messages. {state.error.message ?? ''}
        </p>
      ) : state.isLoading && state.messages.length === 0 ? (
        <p className="text-content-4 px-4 py-4 text-xs">Loading…</p>
      ) : state.messages.length === 0 ? (
        <p className="text-content-3 px-4 py-4 text-sm">
          No pinned messages yet.
        </p>
      ) : (
        // Recycling list so each pinned bubble's per-message Firestore listeners
        // ({@link useLiveMessage} + reactions) stay bounded to the viewport.
        // Top-aligned (pinned-at-desc order reads top→bottom); horizontal
        // padding is per-row since FlashList lays rows out at full width.
        <FlashList<ExtendedCommunityChannelMessage>
          data={state.messages}
          className="min-h-0 flex-1"
          defaultItemSize={96}
          keyExtractor={message => message.id}
          ListHeaderComponent={<div className="pt-4" />}
          ListFooterComponent={<div className="h-4" />}
          renderItem={({ item }) => (
            <div className="px-4 pb-3">
              <ChannelMessageBubble
                message={item}
                currentUserId={currentUserId}
                channelSlug={channel.slug}
                mode={mode}
              />
            </div>
          )}
        />
      )}
    </section>
  );
}
